import { analyzeDocument, type AnalyzedDocument } from '../analyze';
import { applyTextEdits, normalizeEdits, type TextEdit } from '../text/textEdit';
import { ALL_REPAIR_RULES } from './registry';
import {
  type AppliedRepair,
  type CreateRepairEngine,
  DEFAULT_REPAIR_OPTIONS,
  type RepairContext,
  type RepairEngine,
  type RepairOptions,
  type RepairProposal,
  type RepairResult,
  type RepairRule,
  type SkippedRepair,
} from './types';

/**
 * The repair driver: fixed point, conflict resolution, confidence policy and the safety net.
 *
 * `analyze` and `rules` are injected, which is the point of the factory: the three
 * invariants below are tested against a **fake** rule set, with no real rule in the room
 * (ADR-0012, docs/ARCHITECTURE.md §4.4).
 *
 *   1. A document with no `error`-level diagnostic comes back **byte-identical**. Valid JSON
 *      is never touched — the legacy fixer destroyed `[[1,2],[3,4]]` and reported three
 *      phantom fixes for `{"a":{"b":{"c":1}}}` precisely because it had no such gate.
 *   2. Proposals whose edits overlap are **dropped**, never merged by guesswork. The winner
 *      is decided by `priority`, so the outcome is reproducible rather than accidental.
 *   3. If a pass makes the document **worse**, everything is rolled back and the original
 *      text is returned with `stoppedBecause: 'reverted-unsafe'`. The legacy failure mode —
 *      report a list of fixes and hand back a document that still does not parse — is not
 *      reachable from here.
 */
export const createRepairEngine: CreateRepairEngine = (deps): RepairEngine => {
  const rules = deps.rules ?? ALL_REPAIR_RULES;
  const analyze = deps.analyze;

  return {
    repair(source, options) {
      return runToFixedPoint(analyze, rules, source, resolve(options));
    },

    /**
     * Every proposal the enabled rules make for one analysis, ordered the way the driver
     * would apply them.
     *
     * Deliberately NOT conflict-resolved: this feeds the quick-fix menu, where the user
     * applies one fix at a time, and hiding a proposal because another rule happens to touch
     * the same span would hide the only fix the user wanted. Conflict resolution belongs to
     * the batch path, where several edits really do land in one transaction.
     */
    proposalsFor(doc: AnalyzedDocument, options): readonly RepairProposal[] {
      return gather(contextOf(doc), rules, resolve(options)).proposals;
    },
  };
};

/**
 * The default engine: the real pipeline, the real nineteen rules (docs/ARCHITECTURE.md §4.4).
 *
 * Built once at module load rather than per call, because it holds nothing per document —
 * `repair` threads all of its state through its arguments.
 */
const DEFAULT_ENGINE: RepairEngine = createRepairEngine({ analyze: analyzeDocument });

/**
 * `repair(source, options?)` — run every enabled rule to a fixed point over one string.
 *
 * The convenience entry point over `createRepairEngine({ analyze: analyzeDocument })`. The
 * factory still exists and is still what the tests use, because the driver's three
 * invariants have to be provable against a FAKE rule set; this is the wiring, not the logic.
 */
export function repair(source: string, options?: Partial<RepairOptions>): RepairResult {
  return DEFAULT_ENGINE.repair(source, options);
}

function resolve(options?: Partial<RepairOptions>): RepairOptions {
  return { ...DEFAULT_REPAIR_OPTIONS, ...options };
}

/** The frozen view a rule is allowed to read. Identical for every rule of one pass. */
function contextOf(doc: AnalyzedDocument): RepairContext {
  return Object.freeze({
    source: doc.source,
    tokens: doc.tokens,
    tree: doc.tree,
    diagnostics: doc.report.diagnostics,
    lines: doc.lines,
  });
}

interface Gathered {
  readonly proposals: readonly RepairProposal[];
  readonly skipped: readonly SkippedRepair[];
}

/**
 * Run every enabled rule against one context and order the results by `(priority, offset)`.
 *
 * A rule is asked even when it is disabled, because `skipped` is a product feature — it is
 * how the UI explains why a problem the user can see is still there — and "disabled" is only
 * worth saying about a rule that actually had something to propose.
 */
function gather(
  ctx: RepairContext,
  rules: readonly RepairRule[],
  options: RepairOptions,
): Gathered {
  const carrying: Array<{ rule: RepairRule; proposal: RepairProposal; order: number }> = [];
  const skipped: SkippedRepair[] = [];
  let order = 0;

  for (const rule of rules) {
    const proposals = rule.detect(ctx);
    if (proposals.length === 0) continue;

    if (options.disabled.has(rule.id)) {
      note(skipped, { ruleId: rule.id, reason: 'disabled' });
      continue;
    }

    if (rule.confidence === 'heuristic' && !options.allowHeuristic) {
      note(skipped, { ruleId: rule.id, reason: 'heuristic-blocked' });
      continue;
    }

    for (const proposal of proposals) {
      carrying.push({ rule, proposal, order });
      order += 1;
    }
  }

  carrying.sort(
    (a, b) =>
      a.rule.priority - b.rule.priority ||
      startOf(a.proposal) - startOf(b.proposal) ||
      a.order - b.order,
  );

  return { proposals: carrying.map((entry) => entry.proposal), skipped };
}

/** Offset the proposal's first edit lands on — what conflicts are actually about. */
function startOf(proposal: RepairProposal): number {
  let first = proposal.span.from;
  for (const edit of proposal.edits) first = Math.min(first, edit.from);
  return first;
}

/**
 * Walk the ordered proposals, keeping the set of offsets already claimed, and drop any
 * proposal that would write where another has written.
 *
 * The overlap test is `normalizeEdits`' own — two edits conflict when the second starts
 * strictly before the first ends — so what survives here is exactly what `applyTextEdits`
 * accepts. Several insertions at one offset are not a conflict; they are how `{"a":"x` gets
 * both its quote and its brace, in priority order.
 *
 * COST. `claimed` is probed by binary search and written by `splice`; nothing copies it.
 * The first version staged each proposal into `const trial = [...claimed]` and then wrote it
 * back with `claimed.push(...trial)`, which is two O(n) copies PER PROPOSAL and therefore
 * quadratic in the number of defects — 30 000 stray commas cost 4.3 s, and past ~120 000
 * accepted edits the spread-as-arguments write-back hit V8's argument limit and threw
 * `RangeError: Maximum call stack size exceeded` from inside a "documented not to throw"
 * engine. Proposals arrive ordered by `(priority, offset)`, so within a rule each edit lands
 * at the end of `claimed` and the splice is an append; the measured 30 000-comma case is now
 * ~60 ms. `bindFixes` was hardened against this same shape in `analyze.ts`; the driver was
 * not.
 */
function withoutConflicts(proposals: readonly RepairProposal[]): Gathered {
  const claimed: TextEdit[] = [];
  const accepted: RepairProposal[] = [];
  const skipped: SkippedRepair[] = [];
  /** Where each of this proposal's edits belongs in `claimed`, reused across proposals. */
  const points: number[] = [];

  for (const proposal of proposals) {
    const ordered = normalizeEdits(proposal.edits);
    if (ordered === null || ordered.length === 0) {
      note(skipped, { ruleId: proposal.ruleId, reason: 'edit-conflict' });
      continue;
    }

    points.length = 0;
    let fits = true;

    // `ordered` is sorted and internally non-overlapping (that is what `normalizeEdits`
    // guarantees), so each edit can be tested against the UNCHANGED `claimed` and the
    // siblings cannot disagree with one another.
    for (const edit of ordered) {
      const at = insertionPoint(claimed, edit);
      const previous = claimed[at - 1];
      const next = claimed[at];
      if (previous !== undefined && edit.from < previous.to) fits = false;
      else if (next !== undefined && next.from < edit.to) fits = false;
      if (!fits) break;
      points.push(at);
    }

    if (!fits) {
      note(skipped, { ruleId: proposal.ruleId, reason: 'edit-conflict' });
      continue;
    }

    // Descending, so an insertion never invalidates an index still to be used. Equal
    // insertion points keep `ordered`'s own sequence, which is `normalizeEdits`' stable one.
    for (let i = ordered.length - 1; i >= 0; i -= 1) {
      const at = points[i];
      const edit = ordered[i];
      if (at === undefined || edit === undefined) continue;
      if (at === claimed.length) claimed.push(edit);
      else claimed.splice(at, 0, edit);
    }

    accepted.push(proposal);
  }

  return { proposals: accepted, skipped };
}

/** First index of `edits` whose `(from, to)` is not before `edit`'s. */
function insertionPoint(edits: readonly TextEdit[], edit: TextEdit): number {
  let low = 0;
  let high = edits.length;

  while (low < high) {
    const mid = (low + high) >>> 1;
    const other = edits[mid];
    if (other === undefined) break;
    const before = other.from < edit.from || (other.from === edit.from && other.to <= edit.to);
    if (before) low = mid + 1;
    else high = mid;
  }

  return low;
}

/** `skipped` carries only a rule id and a reason, so repeats of one pair say nothing new. */
function note(skipped: SkippedRepair[], entry: SkippedRepair): void {
  const known = skipped.some(
    (other) => other.ruleId === entry.ruleId && other.reason === entry.reason,
  );
  if (!known) skipped.push(entry);
}

function runToFixedPoint(
  analyze: (source: string) => AnalyzedDocument,
  rules: readonly RepairRule[],
  source: string,
  options: RepairOptions,
): RepairResult {
  const original = analyze(source);
  const before = original.report;

  // Invariant 1. Nothing below this line can run on a document that is already valid.
  if (before.counts.errors === 0) {
    return {
      text: source,
      changed: false,
      applied: [],
      skipped: [],
      before,
      after: before,
      passes: 0,
      stoppedBecause: 'already-clean',
    };
  }

  const applied: AppliedRepair[] = [];
  const skipped: SkippedRepair[] = [];
  let text = source;
  let doc = original;
  let passes = 0;
  let stoppedBecause: RepairResult['stoppedBecause'] = 'max-passes';

  while (passes < options.maxPasses) {
    const gathered = gather(contextOf(doc), rules, options);
    for (const entry of gathered.skipped) note(skipped, entry);

    const resolved = withoutConflicts(gathered.proposals);
    for (const entry of resolved.skipped) note(skipped, entry);

    if (resolved.proposals.length === 0) {
      stoppedBecause = 'fixed-point';
      break;
    }

    const edits = resolved.proposals.flatMap((proposal) => [...proposal.edits]);
    // Throws on overlap — which `withoutConflicts` has just made impossible. The throw is
    // the assertion that it did its job (ADR-0012, step 5).
    const nextText = applyTextEdits(text, edits);
    passes += 1;

    if (nextText === text) {
      stoppedBecause = 'no-progress';
      break;
    }

    const nextDoc = analyze(nextText);

    // Invariant 3. Worse than what the user gave us: drop everything, say so, keep the
    // repairs we could not make rather than a document they cannot undo.
    if (nextDoc.report.counts.errors > before.counts.errors) {
      return {
        text: source,
        changed: false,
        applied: [],
        skipped,
        before,
        after: before,
        passes,
        stoppedBecause: 'reverted-unsafe',
      };
    }

    for (const proposal of resolved.proposals) {
      applied.push(describe(proposal, text, doc, passes));
    }

    const remaining = nextDoc.report.counts.errors;
    const previous = doc.report.counts.errors;
    text = nextText;
    doc = nextDoc;

    if (remaining === 0) {
      stoppedBecause = 'fixed-point';
      break;
    }

    // Oscillation guard: a pass that did not strictly reduce the error count will not do
    // better on the next run against a document it has already seen the shape of.
    if (remaining >= previous) {
      stoppedBecause = 'no-progress';
      break;
    }
  }

  return {
    text,
    changed: text !== source,
    applied,
    skipped,
    before,
    after: doc.report,
    passes,
    stoppedBecause,
  };
}

/**
 * Turn an applied proposal into the honest report of what changed: the position in the text
 * as it was **before this pass**, and the exact `before`/`after` slices.
 *
 * Both slices are computed from the proposal's own edit region rather than from `span`, so
 * a rule that highlights a wider region for the user (`close-open-containers` points at the
 * bracket that was never closed) still reports precisely the characters it wrote.
 */
function describe(
  proposal: RepairProposal,
  text: string,
  doc: AnalyzedDocument,
  pass: number,
): AppliedRepair {
  let from = Number.MAX_SAFE_INTEGER;
  let to = 0;
  for (const edit of proposal.edits) {
    from = Math.min(from, edit.from);
    to = Math.max(to, edit.to);
  }
  if (proposal.edits.length === 0) {
    from = proposal.span.from;
    to = proposal.span.to;
  }

  const region = text.slice(from, to);
  const local = proposal.edits.map((edit) => ({
    from: edit.from - from,
    to: edit.to - from,
    insert: edit.insert,
  }));

  const base = {
    ruleId: proposal.ruleId,
    pass,
    at: doc.lines.positionAt(from),
    labelCode: proposal.labelCode,
    before: region,
    after: applyTextEdits(region, local),
  };

  return proposal.labelParams === undefined ? base : { ...base, labelParams: proposal.labelParams };
}
